import { useMemo, useSyncExternalStore } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useOrganization } from "@/contexts/OrganizationContext";
import type { Inventory, InventoryCount } from "@/lib/inventoryTypes";
import {
  confirmPendingCount,
  discardPendingCounts,
  isObsoleteCountWrite,
  pendingCountsKey,
  refusePendingCount,
  subscribePendingCounts,
  type PendingCounts,
} from "@/lib/inventoryPendingCounts";

const STALE_MS = 30 * 1000;

interface InventoryListRow extends Inventory {
  counts_count: number;
}

/** Liste des inventaires de l'organisation (du plus récent au plus ancien). */
export function useInventories() {
  const { organizationId } = useOrganization();

  return useQuery<Inventory[], Error>({
    queryKey: ["inventories", organizationId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("inventories")
        .select("*")
        .eq("organization_id", organizationId)
        .order("inventory_date", { ascending: false })
        .order("created_at", { ascending: false });

      if (error) throw new Error(error.message);
      return data ?? [];
    },
    enabled: !!organizationId,
    staleTime: STALE_MS,
  });
}

/** Détail d'un inventaire (sans les counts). */
export function useInventory(inventoryId: string | null | undefined) {
  return useQuery<Inventory | null, Error>({
    queryKey: ["inventory", inventoryId],
    queryFn: async () => {
      if (!inventoryId) return null;
      const { data, error } = await supabase
        .from("inventories")
        .select("*")
        .eq("id", inventoryId)
        .maybeSingle();

      if (error) throw new Error(error.message);
      return data ?? null;
    },
    enabled: !!inventoryId,
    staleTime: STALE_MS,
  });
}

/** Historique : N derniers inventaires + tous leurs counts en une seule requête combinée. */
export function useInventoryHistory(limit: number) {
  const { organizationId } = useOrganization();

  return useQuery<
    { inventories: Inventory[]; counts: InventoryCount[] },
    Error
  >({
    queryKey: ["inventory-history", organizationId, limit],
    queryFn: async () => {
      const { data: invs, error: invErr } = await supabase
        .from("inventories")
        .select("*")
        .eq("organization_id", organizationId)
        .order("inventory_date", { ascending: false })
        .order("created_at", { ascending: false })
        .limit(limit);

      if (invErr) throw new Error(invErr.message);
      const inventories = invs ?? [];

      if (inventories.length === 0) {
        return { inventories: [], counts: [] };
      }

      const ids = inventories.map((i) => i.id);
      const { data: counts, error: cntErr } = await supabase
        .from("inventory_counts")
        .select("*")
        .in("inventory_id", ids);

      if (cntErr) throw new Error(cntErr.message);

      return { inventories, counts: counts ?? [] };
    },
    enabled: !!organizationId,
    staleTime: STALE_MS,
  });
}

/** Comptages d'un inventaire (indexés par item_id côté consommateur). */
export function useInventoryCounts(inventoryId: string | null | undefined) {
  return useQuery<InventoryCount[], Error>({
    queryKey: ["inventory-counts", inventoryId],
    queryFn: async () => {
      if (!inventoryId) return [];
      const { data, error } = await supabase
        .from("inventory_counts")
        .select("*")
        .eq("inventory_id", inventoryId);

      if (error) throw new Error(error.message);
      return data ?? [];
    },
    enabled: !!inventoryId,
    staleTime: STALE_MS,
  });
}

/** Brouillon en cours pour l'organisation, ou null. */
export function useDraftInventory() {
  const { organizationId } = useOrganization();

  return useQuery<Inventory | null, Error>({
    queryKey: ["inventory-draft", organizationId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("inventories")
        .select("*")
        .eq("organization_id", organizationId)
        .eq("status", "draft")
        .maybeSingle();

      if (error) throw new Error(error.message);
      return data ?? null;
    },
    enabled: !!organizationId,
    staleTime: STALE_MS,
  });
}

export interface StartInventoryPayload {
  inventory_date: string;
  operator_name: string;
}

/** Démarre un nouvel inventaire (brouillon). Échoue si un brouillon existe déjà. */
export function useStartInventory() {
  const qc = useQueryClient();
  const { organizationId, organization, user } = useOrganization();

  return useMutation<Inventory, Error, StartInventoryPayload>({
    mutationFn: async (payload) => {
      if (!user) throw new Error("Utilisateur non connecté");
      if (!organization) throw new Error("Organisation introuvable");

      const { data: nextNumber, error: numErr } = await supabase.rpc(
        "get_next_inventory_number",
        { org_id: organizationId, org_code: organization.code },
      );

      if (numErr) throw new Error(numErr.message);

      const { data, error } = await supabase
        .from("inventories")
        .insert({
          organization_id: organizationId,
          inventory_number: nextNumber as string,
          inventory_date: payload.inventory_date,
          operator_name: payload.operator_name.trim(),
          status: "draft",
          created_by: user.id,
        })
        .select()
        .single();

      if (error) throw new Error(error.message);
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["inventories"] });
      qc.invalidateQueries({ queryKey: ["inventory-draft"] });
    },
  });
}

/** Clé des écritures de comptage : permet de savoir s'il reste des saisies pas encore en base (`useIsMutating`). */
export const INVENTORY_COUNT_MUTATION_KEY = ["inventory-count-upsert"] as const;

/** Erreur d'écriture d'un comptage, avec le statut HTTP (0 = pas de réponse du serveur). */
class CountWriteError extends Error {
  status: number;

  constructor(message: string, status: number) {
    super(message);
    this.status = status;
  }
}

/**
 * Erreur passagère (réseau absent ou instable, serveur indisponible, session à rafraîchir) :
 * l'écriture est retentée sans limite. Les autres (refus RLS, contrainte) sont définitives.
 */
function isTransientCountError(error: Error): boolean {
  if (!(error instanceof CountWriteError)) return true;
  const { status } = error;
  return status === 0 || status === 401 || status === 408 || status === 429 || status >= 500;
}

/** Motif d'un refus définitif, lisible par l'équipe du salon (le message brut est technique). */
export function describeCountRefusal(error: Error): string {
  const status = error instanceof CountWriteError ? error.status : 0;
  if (status === 403) {
    return "Enregistrement refusé : l'inventaire a peut-être été validé entre-temps.";
  }
  if (status === 409) {
    return "Enregistrement refusé : l'article ou l'inventaire n'existe peut-être plus.";
  }
  return `Enregistrement refusé par le serveur (erreur ${status}).`;
}

/** Écriture d'un comptage. `rev` : version de la saisie gardée sur l'appareil (inventoryPendingCounts). */
export interface UpsertCountVariables {
  inventory_id: string;
  item_id: string;
  quantity: number | null;
  rev?: string;
}

/** Upsert d'un comptage (saisie au fil de l'eau, auto-save). */
export function useUpsertCount() {
  const qc = useQueryClient();

  return useMutation<InventoryCount | null, Error, UpsertCountVariables>({
    mutationKey: INVENTORY_COUNT_MUTATION_KEY,
    // Écritures envoyées une par une, dans l'ordre de saisie. Sans cela, les saisies
    // faites sans réseau (réserve, chambre froide) repartaient toutes en même temps au
    // retour de la connexion et arrivaient en base dans le désordre : une valeur
    // intermédiaire (le 1 de 1, 2, 3, 4, 5 tapés au « + ») écrasait la dernière.
    // Constaté sur les inventaires de Lyon (signalé le 19/10/2026).
    scope: { id: "inventory-counts" },
    // Réseau absent ou instable : on retente indéfiniment, de plus en plus espacé (30 s au plus).
    // La saisie reste gardée sur l'appareil (inventoryPendingCounts) jusqu'à confirmation.
    retry: (failureCount, error) => isTransientCountError(error) || failureCount < 2,
    retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 30_000),
    mutationFn: async ({ inventory_id, item_id, quantity, rev }) => {
      // Vérifié à chaque essai, reprises comprises : une version dépassée n'écrit rien
      if (rev && isObsoleteCountWrite(inventory_id, item_id, rev)) return null;
      const { data, error, status } = await supabase
        .from("inventory_counts")
        .upsert(
          {
            inventory_id,
            item_id,
            quantity,
            counted_at: quantity === null ? null : new Date().toISOString(),
          },
          { onConflict: "inventory_id,item_id" },
        )
        .select()
        .single();

      if (error) throw new CountWriteError(error.message, status);
      return data;
    },
    onSuccess: (_data, variables) => {
      if (variables.rev) {
        confirmPendingCount(variables.inventory_id, variables.item_id, variables.rev);
      }
      qc.invalidateQueries({ queryKey: ["inventory-counts", variables.inventory_id] });
    },
    // Seuls les refus définitifs arrivent ici (les erreurs passagères sont retentées)
    onError: (error, variables) => {
      if (variables.rev) {
        refusePendingCount(
          variables.inventory_id,
          variables.item_id,
          variables.rev,
          describeCountRefusal(error),
          error instanceof CountWriteError ? error.status : undefined,
        );
      }
    },
  });
}

/** Saisies de cet inventaire gardées sur l'appareil et pas encore confirmées par la base. */
export function usePendingCounts(inventoryId: string | null | undefined): PendingCounts {
  const raw = useSyncExternalStore(subscribePendingCounts, () => {
    if (!inventoryId) return null;
    try {
      return localStorage.getItem(pendingCountsKey(inventoryId));
    } catch {
      return null;
    }
  });
  return useMemo(() => {
    if (!raw) return {};
    try {
      return JSON.parse(raw) as PendingCounts;
    } catch {
      return {};
    }
  }, [raw]);
}

/** Met à jour l'opérateur ou la date d'un inventaire (uniquement en brouillon). */
export function useUpdateInventory() {
  const qc = useQueryClient();

  return useMutation<
    Inventory,
    Error,
    { id: string; operator_name?: string; inventory_date?: string }
  >({
    mutationFn: async ({ id, ...patch }) => {
      const cleaned: Record<string, unknown> = {};
      if (patch.operator_name !== undefined)
        cleaned.operator_name = patch.operator_name.trim();
      if (patch.inventory_date !== undefined)
        cleaned.inventory_date = patch.inventory_date;

      const { data, error } = await supabase
        .from("inventories")
        .update(cleaned)
        .eq("id", id)
        .select()
        .single();

      if (error) throw new Error(error.message);
      return data;
    },
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: ["inventory", id] });
      qc.invalidateQueries({ queryKey: ["inventories"] });
      qc.invalidateQueries({ queryKey: ["inventory-draft"] });
    },
  });
}

/** Valide l'inventaire (status draft → validated). */
export function useValidateInventory() {
  const qc = useQueryClient();

  return useMutation<Inventory, Error, { id: string }>({
    mutationFn: async ({ id }) => {
      const { data, error } = await supabase
        .from("inventories")
        .update({ status: "validated" })
        .eq("id", id)
        .select()
        .single();

      if (error) throw new Error(error.message);
      return data;
    },
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: ["inventory", id] });
      qc.invalidateQueries({ queryKey: ["inventories"] });
      qc.invalidateQueries({ queryKey: ["inventory-draft"] });
    },
  });
}

/** Repasse en brouillon un inventaire validé (correction admin). */
export function useReopenInventory() {
  const qc = useQueryClient();

  return useMutation<Inventory, Error, { id: string }>({
    mutationFn: async ({ id }) => {
      const { data, error } = await supabase
        .from("inventories")
        .update({ status: "draft" })
        .eq("id", id)
        .select()
        .single();

      if (error) throw new Error(error.message);
      return data;
    },
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: ["inventory", id] });
      qc.invalidateQueries({ queryKey: ["inventories"] });
      qc.invalidateQueries({ queryKey: ["inventory-draft"] });
    },
  });
}

/** Supprime un inventaire (admin uniquement, cascade les counts). */
export function useDeleteInventory() {
  const qc = useQueryClient();

  return useMutation<void, Error, { id: string }>({
    mutationFn: async ({ id }) => {
      const { error } = await supabase.from("inventories").delete().eq("id", id);
      if (error) throw new Error(error.message);
    },
    onSuccess: (_data, { id }) => {
      discardPendingCounts(id);
      qc.invalidateQueries({ queryKey: ["inventories"] });
      qc.invalidateQueries({ queryKey: ["inventory-draft"] });
    },
  });
}
